import cx from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { FunctionComponent, useEffect, useMemo } from 'react';

import useLinkProps from '../../../../hooks/use-link-props';
import ProductCard from '../card';
import classes from '../products.module.css';
import Sort from '../sort';

import generatePageHref from './generate-page-href';
import generatePagination from './generate-pagination';
import getCheverlon from './get-cheverlon';
import { useGenerateProduct } from './use-generate-product-href';
import ViewMobile from '../../../ui/icons/view-mobile';
import ViewDesktop from '../../../ui/icons/view-desktop';
import { useAtom } from 'jotai';
import { viewSelection } from '../../../../state/selection';
import Button from '../../../ui/button';
import { useQuery } from '@tanstack/react-query';
import getCart from '../../../../utils/react-query/query-options/get-cart';
import { cartAtom, cartOpen } from '../../../../state/cart';
import Cookies from 'js-cookie';
import useIsMobile from '../../../../hooks/use-is-mobile';
import ShippingCostInfo from '../../../modules/shipping-cost-info';
import { useAppContext } from '../../../providers/app-context';
import { checkPartAvailabity } from '../../../../utils/check-part-availabilty';
import { loadShippingLocations } from '../../../modules/shipping-selector';

const Results: FunctionComponent<{
  results: TProductSearch['results'];
  others_with_reference: TProductSearch['others_with_reference'];
  search: TProductSearch['search'];
  totalResults: TProductSearch['results_total'];
  currentPage: TProductSearch['page_total'];
  lastPage: TProductSearch['page_total'];
  isLoading: boolean;
  isReferencePage?: boolean;
  isAdvancedSearch?: boolean;
  someOnlyProAvailable?: boolean;
  groups: TCategories;
}> = ({
  results,
  totalResults,
  currentPage,
  lastPage,
  isLoading,
  isReferencePage = false,
  isAdvancedSearch = false,
  someOnlyProAvailable = false,
  groups,
}) => {
    const { detailedPrice, shippingLocation } = useAppContext();
    const { locale = 'en' } = useRouter();
    const { push, query } = useRouter();
    const isMobile = useIsMobile();
    const getLink = useLinkProps();
    const getProductLink = useGenerateProduct(isAdvancedSearch);
    const [view, setView] = useAtom(viewSelection);
    const [open] = useAtom(cartOpen);
    const pages = generatePagination(currentPage, lastPage);
    const { t } = useTranslation();
    const [shoppingCart, setShoppingCart] = useAtom(cartAtom);

    const { data: shippings } = useQuery(
      ['user-dependent', 'shipping-locations-list', locale, shippingLocation],
      loadShippingLocations
    );

    const shipping = useMemo(() => {
      if (shippings) {
        const currentShipping = shippings.find((s) => s.selected);
        return currentShipping;
      }

      return undefined;
    }, [shippings]);

    useEffect(() => {
      if (!isMobile) {
        setView(Cookies.get('_page_view') || 'DESKTOP');
      } else {
        setView('MOBILE');
      }
    }, [isMobile]);

    const {
      data,
      isFetching,
      refetch: updateProductsList,
    } = useQuery(...getCart({ locale, isAdvancedView: isAdvancedSearch, detailedPrice: detailedPrice ? detailedPrice : false }), {
      keepPreviousData: true,
      refetchOnWindowFocus: 'always',
      refetchInterval: 60000,
      refetchOnMount: true,
      staleTime: 10000,
      onSuccess: (data) => {
        setShoppingCart(
          data.products.map((product) => {
            return product.id;
          })
        );
      },
    });

    /*useEffect(() => {
      updateProductsList();
    }, [open]);*/

    if (!results.length) {
      return <div />;
    }

    return (
      <div
        className={cx('relative px-[2px]', {
          'opacity-25': isLoading,
        })}
      >
        <div className={cx('flex', 'items-center pb-4 max-lg:py-2')}>
          <div
            className={cx(
              'border-r border-r-neutral-30 flex items-center justify-center',
              { 'px-4': typeof detailedPrice === 'boolean' }
            )}
          >
            <ShippingCostInfo />
          </div>
          <div
            className={cx(
              'flex-1',
              'font-bold',
              'text-sm',
              'leading-[48px]',
              typeof detailedPrice === 'boolean' ? 'pl-4' : 'pl-4 lg:pl-0'
            )}
          >
            {t('products:totalResults', { qtt: totalResults })}
          </div>
          {isAdvancedSearch && (
            <span className={cx('text-brand-100', 'text-sm', 'font-bold')}>
              {t('advanced-search:products.vat-excluded')}
            </span>
          )}
          <div className="max-lg:hidden">
            <Sort
              hideMobile={false}
              hideDesktop={false}
              onChange={(e) => {
                const { sort: currentSort, page, ...rest } = query;
                const value = e.currentTarget.value;

                const link = getLink('search', {
                  ...rest,
                  ...(value === 'price-asc' ? {} : { sort: value }),
                });
                push(link.href, link.as, {
                  shallow: true,
                });
              }}
            />
          </div>
          <div className="max-lg:hidden flex flex-row">
            <Button
              onClick={(e) => {
                e.preventDefault();
                setView('DESKTOP');
                Cookies.set('_page_view', 'DESKTOP');
              }}
            >
              <ViewDesktop
                className={cx('h-4', {
                  'text-neutral-30': view === 'MOBILE',
                })}
              ></ViewDesktop>
            </Button>
            <Button
              onClick={(e) => {
                e.preventDefault();
                setView('MOBILE');
                Cookies.set('_page_view', 'MOBILE');
              }}
            >
              <ViewMobile
                className={cx('h-4', {
                  'text-neutral-30': view === 'DESKTOP',
                })}
              ></ViewMobile>
            </Button>
          </div>
        </div>
        <ul
          className={cx(
            'max-lg:flex max-lg:flex-col px-4 lg:px-0 max-lg:gap-4 lg:pb-8',
            {
              [classes.productCardGapTabletView]: view === 'MOBILE' && !isMobile,
            },
            {
              'max-lg:pb-8': pages.length <= 1,
            }
          )}
        >
          {(view || isMobile) &&
            results.map((item, index) => {
              const availability = checkPartAvailabity(groups, item.category_slug, item.part_en);
              return (
                <li
                  key={item.sku}
                  className={cx(
                    view === 'DESKTOP' ? classes.listItem : '',
                    'relative'
                  )}
                >
                  <Link
                    href={getProductLink(item.category_slug, item.slug)}
                    className="block"
                    onClick={() => {
                      //setReferer('search');
                      window?.dataLayer.push({
                        event: 'eec.impressionClick',
                        ecommerce: {
                          currencyCode: 'EUR', // currency code
                          click: {
                            actionField: {
                              list: 'Search Results', // List name where the impression click occured
                            },
                            products: [
                              {
                                id: item.sku, // SKU of the product
                                name: item.part_en, // name of the product.
                                brand: item.car_brand, // car brand of the part
                                category: item.category_en_old, // Product category(ies) - each "/" is a category level (max 5 levels)
                                price: item.pricing?.price_product
                                  ? Number(item.pricing.price_product).toFixed(2)
                                  : null, // price of the part without shipping, tax and currency
                                position: index, // the position of the item in the list
                                dimension2: item.reference, // reference of the product
                                dimension3: item.car_model, // car model of the part
                                dimension4: item?.car_version, // car version of the part
                              },
                            ],
                          },
                        },
                      });

                      // GA4
                      window?.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
                      window?.dataLayer.push({
                        event: 'select_item',
                        ecommerce: {
                          currency: 'EUR',
                          items: [
                            {
                              item_name: item.part_en,
                              item_id: item.sku,
                              price: item.pricing?.price_product
                                ? Number(item.pricing.price_product).toFixed(2)
                                : null,
                              item_brand: item.car_brand,
                              item_category: item.category_en,
                              item_category2: item.part_en,
                              item_list_name: 'Search Results',
                              item_list_id: 'search-results',
                              index: index,
                              quantity: '1',
                            },
                          ],
                        },
                      });
                    }}
                  >
                    <ProductCard
                      item={item}
                      actState={
                        data?.products?.find((p) => p.id === item.id)
                          ? 'ADDED'
                          : undefined
                      }
                      index={index}
                      view={view}
                      availability={availability}
                      shipping={shipping}
                    />
                  </Link>
                </li>
              )
            })}
        </ul>
        {pages.length > 1 && (
          <div
            className={cx(
              'grid',
              'justify-center',
              'mt-4',
              'lg:mt-0 mb-4 lg:mb-8'
            )}
          >
            <ul className={cx('flex')}>
              {currentPage > 1 &&
                getCheverlon(
                  generatePageHref(
                    query,
                    currentPage - 1,
                    getLink,
                    isReferencePage,
                    isAdvancedSearch
                  ),
                  classes.cheverlonLeft
                )}
              {pages.map((page) => (
                <li key={page}>
                  <Link
                    {...generatePageHref(
                      query,
                      page,
                      getLink,
                      isReferencePage,
                      isAdvancedSearch
                    )}
                    shallow
                    scroll
                    prefetch={false}
                    className={cx(
                      'grid',
                      'w-8',
                      'h-8',
                      'justify-center',
                      'items-center',
                      'rounded-full',
                      'hover:bg-neutral-10',
                      { 'bg-brand-10': page === currentPage }
                    )}
                  >
                    {page}
                  </Link>
                </li>
              ))}
              {currentPage < lastPage &&
                getCheverlon(
                  generatePageHref(
                    query,
                    currentPage + 1,
                    getLink,
                    isReferencePage,
                    isAdvancedSearch
                  ),
                  classes.cheverlonRight
                )}
            </ul>
          </div>
        )}
      </div>
    );
  };

export default Results;
